import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import chef1Img from "../assets/chef1.jpg";
import chef2Img from "../assets/chef2.jpg";
import chef3Img from "../assets/chef3.jpg";
import chef4Img from "../assets/chef4.jpg";
import prawnImg from "../assets/prawn.jpg";
import muttonImg from "../assets/mutton.jpg";
import chickenImg from "../assets/chicken.jpg";
import vegImg from "../assets/veg.jpg";
import fishImg from "../assets/fish.jpg";
import crabImg from "../assets/crab.jpg";

const signatureDishes = [
	{
		name: "Prawn Biryani",
		image: prawnImg,
		desc: "Royal prawns marinated in chef’s secret spices, layered with fragrant basmati rice and slow-cooked for a melt-in-the-mouth experience. A true seafood lover’s delight.",
	},
	{
		name: "Mutton Biryani",
		image: muttonImg,
		desc: "Succulent mutton pieces, slow-cooked to perfection and layered with aromatic rice. Rich, hearty, and bursting with traditional flavors.",
	},
	{
		name: "Chicken Dum Biryani",
		image: chickenImg,
		desc: "Juicy chicken marinated overnight and cooked on dum with authentic spices. Each bite is a celebration of Hyderabadi tradition.",
	},
	{
		name: "Veg Biryani",
		image: vegImg,
		desc: "A medley of garden-fresh vegetables and aromatic herbs, layered with saffron rice. Perfect for vegetarians and flavor lovers alike.",
	},
	{
		name: "Fish Fry",
		image: fishImg,
		desc: "Fresh fish fillets marinated in house spices and shallow-fried until golden and crispy. Served with a tangy dip for a coastal treat.",
	},
	{
		name: "Crab Roast",
		image: crabImg,
		desc: "Tender crab cooked in a spicy masala, roasted to perfection for a rich and unforgettable taste. A must-try for spice enthusiasts.",
	},
];

const dishVariants = {
	hidden: { opacity: 0, y: 40, scale: 0.95 },
	visible: (i) => ({
		opacity: 1,
		y: 0,
		scale: 1,
		transition: { delay: i * 0.13 + 0.2, duration: 0.7, type: "spring", bounce: 0.28 },
	}),
	hover: {
		scale: 1.045,
		boxShadow: "0 8px 32px #ffd70033, 0 2px 16px #fff4",
		transition: { duration: 0.18 },
	},
};

const Home = () => {
	const navigate = useNavigate();

	return (
		<div
			style={{
				minHeight: "100vh",
				fontFamily: "'Poppins', 'Inter', 'Segoe UI', Arial, sans-serif",
				background: "linear-gradient(135deg, #fffbe7cc 0%, #e0e7ffcc 100%)",
				padding: 0,
				margin: 0,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
			}}
		>
			{/* Hero Section */}
			<motion.section
				initial="hidden"
				animate="visible"
				variants={{
					hidden: { opacity: 0, y: -40 },
					visible: { opacity: 1, y: 0, transition: { duration: 1, ease: "easeOut" } },
				}}
				style={{
					width: "100%",
					maxWidth: 1050,
					minHeight: "65vh",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					padding: "5.5rem 1.5rem 3.5rem 1.5rem",
					background: "linear-gradient(120deg, #fffbe7 60%, #ffe066 100%)",
					borderBottomLeftRadius: "3rem",
					borderBottomRightRadius: "3rem",
					boxShadow: "0 12px 48px #ffd70044, 0 2px 16px #ffd70022",
					margin: "0 auto",
					position: "relative",
					overflow: "hidden",
				}}
			>
				{/* Royal Crown SVG Accent */}
				<motion.div
					initial={{ opacity: 0, scale: 0.7, y: -30 }}
					animate={{ opacity: 1, scale: 1, y: 0 }}
					transition={{ duration: 1, delay: 0.2, type: "spring", stiffness: 80 }}
					style={{
						marginBottom: "1.2rem",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<svg width="70" height="48" viewBox="0 0 70 48" fill="none" xmlns="http://www.w3.org/2000/svg">
						<ellipse cx="35" cy="44" rx="28" ry="4" fill="#ffd700" fillOpacity="0.18"/>
						<path d="M10 36L18 14L35 32L52 14L60 36H10Z" fill="#ffd700" stroke="#bfa100" strokeWidth="2"/>
						<circle cx="18" cy="14" r="3" fill="#fffbe7" stroke="#ffd700" strokeWidth="2"/>
						<circle cx="52" cy="14" r="3" fill="#fffbe7" stroke="#ffd700" strokeWidth="2"/>
						<circle cx="35" cy="32" r="3" fill="#fffbe7" stroke="#ffd700" strokeWidth="2"/>
					</svg>
				</motion.div>
				<motion.h1
					initial={{ opacity: 0, y: -30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, ease: "easeOut" }}
					style={{
						color: "#bfa100",
						textAlign: "center",
						fontWeight: 900,
						fontSize: "3.3rem",
						letterSpacing: "0.06em",
						marginBottom: "1.1rem",
						textShadow: "0 4px 24px #ffd70088, 0 2px 12px #ffd70022",
						lineHeight: 1.1,
						maxWidth: 700,
						fontFamily: "'Playfair Display', 'Poppins', serif",
					}}
				>
					Hungry King
				</motion.h1>
				<motion.h2
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.1, duration: 0.8 }}
					style={{
						color: "#22223b",
						textAlign: "center",
						fontWeight: 700,
						fontSize: "2rem",
						letterSpacing: "0.03em",
						marginBottom: "1.3rem",
						textShadow: "0 2px 12px #ffd70033",
						fontFamily: "'Playfair Display', 'Poppins', serif",
					}}
				>
					Experience Royal Indian Flavours
				</motion.h2>
				<motion.p
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.2, duration: 0.8 }}
					style={{
						color: "#22223b",
						fontSize: "1.22rem",
						textAlign: "center",
						marginBottom: "2.5rem",
						lineHeight: 1.7,
						fontWeight: 500,
						maxWidth: 700,
						marginLeft: "auto",
						marginRight: "auto",
						textShadow: "0 2px 12px #fffbe7cc",
						fontFamily: "'Poppins', 'Inter', 'Segoe UI', Arial, sans-serif",
					}}
				>
					Discover the crown jewel of Indian cuisine. Our chefs bring you authentic biryanis, signature seafood, and classic curries, all crafted with premium ingredients and traditional recipes. Every meal is a royal experience!
				</motion.p>
				<motion.button
					whileHover={{
						scale: 1.11,
						background: "linear-gradient(90deg, #fffbe7 0%, #ffd700 100%)",
						color: "#bfa100",
						boxShadow: "0 0 0 6px #ffd70033",
					}}
					whileTap={{ scale: 0.97 }}
					style={{
						background: "linear-gradient(90deg, #ffd700 60%, #ffed85 100%)",
						color: "#001d4a",
						fontWeight: "bold",
						fontSize: "1.35rem",
						borderRadius: "2.5rem",
						boxShadow: "0 4px 32px #ffd70055",
						padding: "1.2rem 3.8rem",
						border: "none",
						transition: "background 0.2s, box-shadow 0.2s",
						letterSpacing: "0.05em",
						outline: "none",
						cursor: "pointer",
						marginTop: "0.5rem",
						fontFamily: "'Poppins', 'Inter', 'Segoe UI', Arial, sans-serif",
						textShadow: "0 2px 8px #fffbe7cc",
					}}
					onClick={() => navigate("/menu")}
				>
					Explore Our Menu
				</motion.button>
				{/* Decorative gold sparkles */}
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 0.18 }}
					transition={{ delay: 0.7, duration: 1.2 }}
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						pointerEvents: "none",
						background: "radial-gradient(circle at 20% 30%, #ffd700 0%, transparent 60%), radial-gradient(circle at 80% 70%, #ffd700 0%, transparent 60%)",
						zIndex: 0,
					}}
				/>
			</motion.section>

			{/* Signature Dishes */}
			<motion.h2
				initial={{ opacity: 0, y: -20 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true }}
				transition={{ delay: 0.1, duration: 0.7 }}
				style={{
					color: "#ffd700",
					textAlign: "center",
					fontWeight: 800,
					fontSize: "2rem",
					margin: "3.5rem 0 1.5rem 0",
					letterSpacing: "0.03em",
					textShadow: "0 2px 8px #ffd70022",
				}}
			>
				Signature Dishes
			</motion.h2>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
					gap: "2rem",
					maxWidth: 900,
					margin: "0 auto 2.5rem auto",
				}}
			>
				{signatureDishes.map((dish, i) => (
					<motion.div
						key={dish.name}
						custom={i}
						variants={dishVariants}
						initial="hidden"
						whileInView="visible"
						viewport={{ once: true }}
						whileHover="hover"
						style={{
							background: "rgba(255,255,255,0.8)",
							borderRadius: "2rem",
							boxShadow: "0 4px 24px #ffd70022",
							padding: "1.5rem 1rem 1.2rem 1rem",
							textAlign: "center",
							border: "1.5px solid #f1f5f9",
							transition: "box-shadow 0.2s, background 0.2s",
							backdropFilter: "blur(6px)",
							minHeight: 360,
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
						}}
					>
						<img
							src={dish.image}
							alt={dish.name}
							style={{
								width: 100,
								height: 100,
								objectFit: "cover",
								borderRadius: "1.2rem",
								boxShadow: "0 2px 12px #ffd70022",
								marginBottom: "1rem",
							}}
						/>
						<h3
							style={{
								color: "#2563eb",
								fontWeight: 700,
								fontSize: "1.18rem",
								marginBottom: "0.5rem",
								letterSpacing: "0.01em",
							}}
						>
							{dish.name}
						</h3>
						<motion.p
							initial={{ opacity: 0, y: 10 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}
							style={{
								color: "#64748b",
								fontSize: "1rem",
								fontWeight: 500,
								margin: 0,
								lineHeight: 1.5,
							}}
						>
							{dish.desc}
						</motion.p>
					</motion.div>
				))}
			</div>

			{/* Chef Section */}
			<motion.section
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true }}
				transition={{ duration: 0.7 }}
				style={{
					background: "rgba(255,255,255,0.93)",
					borderRadius: "2rem",
					boxShadow: "0 4px 24px #ffd70022",
					padding: "2.5rem 1.5rem 2rem 1.5rem",
					margin: "3rem 0",
					maxWidth: 950,
					marginLeft: "auto",
					marginRight: "auto",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
				}}
			>
				<motion.h2
					initial={{ opacity: 0, y: -20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}
					style={{
						color: "#ffd700",
						fontWeight: 800,
						fontSize: "2rem",
						marginBottom: "1.2rem",
						letterSpacing: "0.03em",
						textShadow: "0 2px 8px #ffd70022",
						textAlign: "center",
					}}
				>
					Meet Our Chefs
				</motion.h2>
				<motion.p
					initial={{ opacity: 0, y: 10 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ delay: 0.1, duration: 0.7 }}
					style={{
						color: "#22223b",
						fontSize: "1.13rem",
						fontWeight: 500,
						margin: "0 0 2.2rem 0",
						lineHeight: 1.7,
						textAlign: "center",
						maxWidth: 700,
					}}
				>
					Our culinary team blends tradition and innovation. From royal biryanis to creative seafood, every dish is made with passion, skill, and a love for authentic Indian cooking. Get to know the masters behind your favorite flavors!
				</motion.p>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(2, 1fr)",
						gap: "2.5rem 2.5rem",
						width: "100%",
						maxWidth: 540,
						justifyItems: "center",
					}}
				>
					{/* Chef Card 1 */}
					<motion.div
						whileHover={{ scale: 1.04, boxShadow: "0 8px 32px #ffd70033" }}
						transition={{ type: "spring", stiffness: 200, damping: 15 }}
						style={{
							background: "rgba(255,255,255,0.92)",
							borderRadius: "1.5rem",
							boxShadow: "0 2px 12px #ffd70022",
							padding: "1.5rem 1rem",
							textAlign: "center",
							border: "1.5px solid #f1f5f9",
							width: "100%",
							minWidth: 220,
							transition: "box-shadow 0.2s, background 0.2s",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
						}}
					>
						<img
							src={chef1Img}
							alt="Chef Sameer"
							style={{
								width: 90,
								height: 90,
								borderRadius: "50%",
								objectFit: "cover",
								boxShadow: "0 2px 12px #ffd70022",
								border: "3px solid #ffd700",
								marginBottom: "1rem",
							}}
						/>
						<div style={{ fontWeight: 700, color: "#2563eb", fontSize: "1.08rem" }}>Chef Sameer</div>
						<div style={{ fontSize: "0.99rem", color: "#64748b", margin: "0.5rem 0 0 0" }}>
							Expert in seafood and grilling, bringing bold flavors to every dish.
						</div>
					</motion.div>
					{/* Chef Card 2 */}
					<motion.div
						whileHover={{ scale: 1.04, boxShadow: "0 8px 32px #ffd70033" }}
						transition={{ type: "spring", stiffness: 200, damping: 15 }}
						style={{
							background: "rgba(255,255,255,0.92)",
							borderRadius: "1.5rem",
							boxShadow: "0 2px 12px #ffd70022",
							padding: "1.5rem 1rem",
							textAlign: "center",
							border: "1.5px solid #f1f5f9",
							width: "100%",
							minWidth: 220,
							transition: "box-shadow 0.2s, background 0.2s",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
						}}
					>
						<img
							src={chef2Img}
							alt="Chef Priya"
							style={{
								width: 90,
								height: 90,
								borderRadius: "50%",
								objectFit: "cover",
								boxShadow: "0 2px 12px #ffd70022",
								border: "3px solid #ffd700",
								marginBottom: "1rem",
							}}
						/>
						<div style={{ fontWeight: 700, color: "#2563eb", fontSize: "1.08rem" }}>Chef Priya</div>
						<div style={{ fontSize: "0.99rem", color: "#64748b", margin: "0.5rem 0 0 0" }}>
							Passionate about fresh, local ingredients and creative plating.
						</div>
					</motion.div>
					{/* Chef Card 3 */}
					<motion.div
						whileHover={{ scale: 1.04, boxShadow: "0 8px 32px #ffd70033" }}
						transition={{ type: "spring", stiffness: 200, damping: 15 }}
						style={{
							background: "rgba(255,255,255,0.92)",
							borderRadius: "1.5rem",
							boxShadow: "0 2px 12px #ffd70022",
							padding: "1.5rem 1rem",
							textAlign: "center",
							border: "1.5px solid #f1f5f9",
							width: "100%",
							minWidth: 220,
							transition: "box-shadow 0.2s, background 0.2s",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
						}}
					>
						<img
							src={chef3Img}
							alt="Chef Anjali"
							style={{
								width: 90,
								height: 90,
								borderRadius: "50%",
								objectFit: "cover",
								boxShadow: "0 2px 12px #ffd70022",
								border: "3px solid #ffd700",
								marginBottom: "1rem",
							}}
						/>
						<div style={{ fontWeight: 700, color: "#2563eb", fontSize: "1.08rem" }}>Chef Anjali</div>
						<div style={{ fontSize: "0.99rem", color: "#64748b", margin: "0.5rem 0 0 0" }}>
							Dessert specialist, crafting sweet masterpieces with a royal touch.
						</div>
					</motion.div>
					{/* Chef Card 4 */}
					<motion.div
						whileHover={{ scale: 1.04, boxShadow: "0 8px 32px #ffd70033" }}
						transition={{ type: "spring", stiffness: 200, damping: 15 }}
						style={{
							background: "rgba(255,255,255,0.92)",
							borderRadius: "1.5rem",
							boxShadow: "0 2px 12px #ffd70022",
							padding: "1.5rem 1rem",
							textAlign: "center",
							border: "1.5px solid #f1f5f9",
							width: "100%",
							minWidth: 220,
							transition: "box-shadow 0.2s, background 0.2s",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
						}}
					>
						<img
							src={chef4Img}
							alt="Chef Ranvijay"
							style={{
								width: 90,
								height: 90,
								borderRadius: "50%",
								objectFit: "cover",
								boxShadow: "0 2px 12px #ffd70022",
								border: "3px solid #ffd700",
								marginBottom: "1rem",
							}}
						/>
						<div style={{ fontWeight: 700, color: "#2563eb", fontSize: "1.08rem" }}>Chef Ranvijay</div>
						<div style={{ fontSize: "0.99rem", color: "#64748b", margin: "0.5rem 0 0 0" }}>
							Master of tandoor and North Indian cuisine, known for his aromatic creations.
						</div>
					</motion.div>
				</div>
			</motion.section>

			{/* Footer */}
			<motion.footer
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true }}
				transition={{ duration: 0.7 }}
				style={{
					marginTop: "3rem",
					padding: "2.5rem 0 2.5rem 0",
					background: "linear-gradient(90deg, #ffd700 0%, #fffbe7 100%)",
					borderRadius: "2rem",
					boxShadow: "0 2px 24px #ffd70044",
					maxWidth: 1050,
					marginLeft: "auto",
					marginRight: "auto",
					width: "100%",
				}}
			>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "1fr 1.5fr",
						gap: "2.5rem",
						alignItems: "center",
						justifyContent: "center",
						maxWidth: 900,
						margin: "0 auto",
					}}
				>
					{/* Left: Logo & Founder */}
					<div style={{ textAlign: "center" }}>
						<div style={{ marginBottom: "1.2rem" }}>
							<svg width="60" height="40" viewBox="0 0 70 48" fill="none" xmlns="http://www.w3.org/2000/svg">
								<ellipse cx="35" cy="44" rx="28" ry="4" fill="#ffd700" fillOpacity="0.18"/>
								<path d="M10 36L18 14L35 32L52 14L60 36H10Z" fill="#ffd700" stroke="#bfa100" strokeWidth="2"/>
								<circle cx="18" cy="14" r="3" fill="#fffbe7" stroke="#ffd700" strokeWidth="2"/>
								<circle cx="52" cy="14" r="3" fill="#fffbe7" stroke="#ffd700" strokeWidth="2"/>
								<circle cx="35" cy="32" r="3" fill="#fffbe7" stroke="#ffd700" strokeWidth="2"/>
							</svg>
						</div>
						<div style={{
							color: "#bfa100",
							fontWeight: 900,
							fontSize: "1.5rem",
							fontFamily: "'Playfair Display', serif",
							letterSpacing: "0.04em",
							marginBottom: "0.5rem",
							textShadow: "0 2px 8px #ffd70055"
						}}>
							Hungry King
						</div>
						<div style={{
							color: "#22223b",
							fontWeight: 600,
							fontSize: "1.08rem",
							marginBottom: "0.7rem"
						}}>
						</div>
					</div>
					{/* Right: Message */}
					<div
						style={{
							background: "rgba(255,255,255,0.85)",
							borderRadius: "1.2rem",
							padding: "1.5rem 2rem",
							color: "#22223b",
							fontWeight: 500,
							fontSize: "1.13rem",
							boxShadow: "0 2px 12px #ffd70022",
							border: "1.5px solid #f1f5f9",
							textAlign: "left",
							fontFamily: "'Poppins', 'Inter', 'Segoe UI', Arial, sans-serif",
							position: "relative",
						}}
					>
						<span style={{ fontWeight: 700, color: "#bfa100" }}>
							A message from our restaurateur, Kiran Sai Kanta:
						</span>
						<br />
						<span style={{ display: "block", marginTop: "0.7rem", fontStyle: "italic" }}>
							“At Hungry King, we believe every guest deserves a royal experience. Thank you for letting us serve you the finest Indian flavors. We hope you feel at home and leave with a smile every time you visit.”
						</span>
					</div>
				</div>
			</motion.footer>

		</div>
	);
};

export default Home;
